/**
 * Catalogue des éléments d’UI dont l’affichage web / mobile est administrable.
 * Les overrides sont stockés dans `settings.system.ui_surfaces`.
 */

export const UI_SURFACE_MD_BREAKPOINT = 768;

/** @typedef {{ web: boolean, mobile: boolean }} UiSurfaceFlags */

/** @type {Array<{ id: string, page: string, label: string, default: UiSurfaceFlags }>} */
export const UI_SURFACE_CATALOG = [
  {
    id: 'members.import',
    page: 'Membres',
    label: 'Importer membres',
    default: { web: true, mobile: false },
  },
  {
    id: 'members.import_template',
    page: 'Membres',
    label: 'Modèle CSV (membres)',
    default: { web: true, mobile: false },
  },
  {
    id: 'members.bulk_bar',
    page: 'Membres',
    label: 'Barre d’actions groupées (membres)',
    default: { web: true, mobile: false },
  },
  {
    id: 'members.card_details',
    page: 'Membres',
    label: 'Détails étendus des cartes membres',
    default: { web: true, mobile: false },
  },
  {
    id: 'contributions.import',
    page: 'Cotisations',
    label: 'Importer des paiements',
    default: { web: true, mobile: false },
  },
  {
    id: 'contributions.import_template',
    page: 'Cotisations',
    label: 'Modèle CSV (cotisations)',
    default: { web: true, mobile: false },
  },
  {
    id: 'contributions.bulk_create',
    page: 'Cotisations',
    label: 'Enregistrement en masse',
    default: { web: true, mobile: false },
  },
  {
    id: 'contributions.kpi_grid',
    page: 'Cotisations',
    label: 'Indicateurs KPI (cotisations)',
    default: { web: true, mobile: false },
  },
  {
    id: 'contributions.bulk_bar',
    page: 'Cotisations',
    label: 'Barre d’actions groupées (cotisations)',
    default: { web: true, mobile: false },
  },
  {
    id: 'users.import',
    page: 'Utilisateurs',
    label: 'Importer utilisateurs',
    default: { web: true, mobile: false },
  },
  {
    id: 'users.import_template',
    page: 'Utilisateurs',
    label: 'Modèle CSV (utilisateurs)',
    default: { web: true, mobile: false },
  },
  {
    id: 'financial.kpi_grid',
    page: 'Finances',
    label: 'Indicateurs KPI (finances)',
    default: { web: true, mobile: false },
  },
  {
    id: 'assistance.summary_kpis',
    page: 'Assistance',
    label: 'Résumé KPI assistance',
    default: { web: true, mobile: false },
  },
  {
    id: 'meetings.bulk_bar',
    page: 'Réunions / PV',
    label: 'Sélection multiple (PV)',
    default: { web: true, mobile: false },
  },
  {
    id: 'documents.bulk_bar',
    page: 'Documents',
    label: 'Sélection multiple (documents)',
    default: { web: true, mobile: false },
  },
  {
    id: 'dashboard.member_promo',
    page: 'Accueil membre',
    label: 'Bannière promo (accueil membre)',
    default: { web: true, mobile: false },
  },
  {
    id: 'dashboard.member_manager_hub',
    page: 'Accueil membre',
    label: 'Liens administration (accueil membre)',
    default: { web: true, mobile: false },
  },
];

export const UI_SURFACE_DEFAULTS = UI_SURFACE_CATALOG.reduce((acc, item) => {
  acc[item.id] = { ...item.default };
  return acc;
}, /** @type {Record<string, UiSurfaceFlags>} */ ({}));

/**
 * @param {unknown} raw
 * @returns {Record<string, UiSurfaceFlags>}
 */
export function normalizeUiSurfaces(raw) {
  const out = { ...UI_SURFACE_DEFAULTS };
  if (!raw || typeof raw !== 'object') return out;
  for (const item of UI_SURFACE_CATALOG) {
    const override = raw[item.id];
    if (!override || typeof override !== 'object') continue;
    out[item.id] = {
      web: Object.prototype.hasOwnProperty.call(override, 'web')
        ? Boolean(override.web)
        : item.default.web,
      mobile: Object.prototype.hasOwnProperty.call(override, 'mobile')
        ? Boolean(override.mobile)
        : item.default.mobile,
    };
  }
  return out;
}

/**
 * @param {string} surfaceId
 * @param {object | null | undefined} settings
 * @param {boolean} isMobile
 */
export function resolveUiSurfaceVisible(surfaceId, settings, isMobile) {
  const map = normalizeUiSurfaces(settings?.system?.ui_surfaces);
  const flags = map[surfaceId] || { web: true, mobile: true };
  return isMobile ? Boolean(flags.mobile) : Boolean(flags.web);
}

/**
 * Payload à persister (uniquement les ids du catalogue).
 * @param {unknown} raw
 */
export function buildUiSurfacesPayload(raw) {
  const normalized = normalizeUiSurfaces(raw);
  const out = {};
  for (const item of UI_SURFACE_CATALOG) {
    out[item.id] = {
      web: Boolean(normalized[item.id]?.web),
      mobile: Boolean(normalized[item.id]?.mobile),
    };
  }
  return out;
}
